import { useParams } from 'react-router-dom';
import SideBar from '../../components/global/SideBar';
import {  ClockIcon, InfoIcon, NotebookPen, Share } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { useEffect, useState } from 'react';
import { Loader, Modal, StarRating, useCurrentUser } from '@beholos/holoscommon';
import { usePageTitle } from '../../hooks/usePageTitle';
import { completedVideo, createReview, fetchVideoDetails, type VideoReviews } from '../../services/video.service';
import type { VideoInterface } from '../../types/video.interface';
import { VideoReviewModal } from '../../components/video-profile/VideoReviewModal';
import { ShareVideoModal } from '@beholos/holoscommon/dist/Modal';
import { trackEvent } from '../../services/analytics.service';

function VideoProfile() {
  const { slug } = useParams<{ slug: string }>();
  // Title updates after video is loaded

  const { user, loading: loadingUser } = useCurrentUser();

  const [loading, setLoading] = useState(true);
  const [video, setVideo] = useState<VideoInterface>({} as VideoInterface);
  const [reviews, setReviews] = useState<VideoReviews[]>([]);
  const [watchedSeconds, setWatchedSeconds] = useState<Set<number>>(new Set());
  const [hasReached10Percent, setHasReached10Percent] = useState(false);
  const [showVideoReview, setShowVideoReview] = useState(false);
  const [rating, setRating] = useState(0);
  const [review, setReview] = useState('');
  const [previousReview, setPreviousReview] = useState<VideoReviews | undefined>();
  const [message, setMessage] = useState('');
  const [cancelledReview, setCancelledReview] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);
  const [notFound, setNotFound] = useState(false);

  usePageTitle(video?.title ? `${video.title} - Holos` : 'Video - Holos');


  useEffect(() => {
    if (!slug) {
      console.error('No slug provided');
      return;
    }

    fetchVideoDetails(slug).then((data) => {
      if (data && data.video) {
        setVideo(data.video);
        setReviews(data.reviews || []);
        setPreviousReview(data?.review);
        trackEvent('video_play', { video_title: data.video.title });
      } else {
        setNotFound(true);
      }
    }).catch((error: any) => {
      console.error('Error fetching video details:', error);
      setLoading(false);
    }).finally(() => {
      setLoading(false);
    });
  }, [slug]);

  const handleVideoTimeUpdate = (event: React.SyntheticEvent<HTMLVideoElement>) => {
    const videoElement = event.currentTarget;
    const currentTime = Math.floor(videoElement.currentTime);
    const duration = videoElement.duration;

    if (duration && currentTime >= 0) {
      setWatchedSeconds(prev => new Set(prev).add(currentTime));

      const totalWatchedSeconds = watchedSeconds.size + (watchedSeconds.has(currentTime) ? 0 : 1);
      const totalDurationSeconds = Math.floor(duration);
      const watchedPercentage = (totalWatchedSeconds / totalDurationSeconds) * 100;

      if (watchedPercentage >= 10 && !hasReached10Percent) {
        setHasReached10Percent(true);
        completedVideo(video._id || '').catch((error: any) => {
          console.error('Error marking video as completed:', error);
        });
      }

      if (currentTime >= totalDurationSeconds - 10 && !previousReview && !cancelledReview) {
        setShowVideoReview(true);
      }
    }
  };

  const onSubmitReview = () => {
    if (review.trim() === '' || rating < 0) return;

    createReview({ id: video._id, rating: rating - 1, review }).then(() => {
      setShowVideoReview(false);
      setRating(0);
      setReview('');

      const newReview = {
        rating: rating - 1,
        review,
        user: { firstname: user?.firstname || '' },
        date: new Date().toISOString(),
        userImage: user?.image || ''
      }
      setPreviousReview(newReview)
      setReviews(prev => [
        newReview,
        ...prev,
      ]);
      setMessage('Comentario guardado correctamente. ¡Gracias por tu opinión!');
      setCancelledReview(false);
    }).catch((error: any) => {
      console.error('Error creating review:', error);
      setMessage('Error al crear el comentario. Por favor, inténtalo de nuevo más tarde.');
    });
  }

  const onCancelReview = () => {
    setShowVideoReview(false);
    setCancelledReview(true);
  };

  return (
    <div className="flex h-[calc(100vh-80px)] w-screen overflow-hidden">
      <div className="hidden md:block md:w-80 lg:w-96 h-full overflow-y-auto">
        <SideBar />
      </div>

      <div className="flex-1 h-full overflow-y-auto bg-gray-100 px-10">
        {loading || loadingUser ? (
          <div className='flex justify-center items-center h-full'>
            <Loader />
          </div>
        ) : (
          <div className="my-16 max-w-2xl mx-auto">
            <div>
              <div className="text-gray-600 text-xs text-center mb-2">
                <InfoIcon className="inline-block mr-2" width={15} />
                ¡Por cada video completo que visualices, ganarás HoloPoints que te ayudarán a seguir subiendo de nivel!
                Puedes ganar HoloPoints de esta forma con los 3 primeros videos al día que visualices.
              </div>
            {videoFailed ? (
                <div className="flex flex-col items-center justify-center my-8 text-center bg-white border border-gray-200 rounded-lg p-6">
                  <div className="text-lg font-semibold mb-1">No pudimos cargar el video</div>
                  <div className="text-sm text-gray-600 mb-4">Revisa tu conexión o intenta nuevamente en unos segundos.</div>
                  <div className="flex gap-3">
                  
                    <a
                      className="rounded-xl border border-black bg-white text-black px-5 py-2 text-sm font-medium cursor-pointer"
                      href="/user/clases-online"
                    >
                      Volver a Videos
                    </a>
                  </div>
                </div>
            ) : notFound ? (
              <div className="flex flex-col items-center justify-center my-8 text-center bg-white border border-gray-200 rounded-lg p-6">
                <div className="text-lg font-semibold mb-3">No encontramos este video</div>
                <button
                  className="rounded-xl bg-holos text-white px-5 py-2 text-sm font-medium cursor-pointer"
                  onClick={() => {
                    if (window.history.length > 1) {
                      window.history.back();
                    } else {
                      window.location.href = '/user/clases-online';
                    }
                  }}
                >
                  Volver
                </button>
              </div>
            ) : (
                <div className="flex items-center justify-center my-4">
                  <div className="w-full">
                    <video
                      key={video._id}
                      width="100%"
                      height="800"
                      controls
                      preload="metadata"
                      onTimeUpdate={handleVideoTimeUpdate}
                      onError={() => setVideoFailed(true)}
                      autoPlay={true}
                      muted
                    >
                      <source src={video.mp4Url} type="video/mp4" />
                      Tu navegador no soporta la etiqueta de video.
                    </video>
                  </div>
                </div>
              )}
            </div>

            {!videoFailed && !notFound && (
              <div className="flex justify-end text-sm py-3">
                <div className="flex mr-3">
                  <div
                    className="flex justify-center items-center text-sm cursor-pointer hover:text-holos-dark"
                    onClick={() => setShowShareModal(true)}>
                    <Share className="inline-block mr-2" width={15} />
                    Compartirlo a un amigo
                  </div>
                </div>
              {/* <div className="flex mr-3">
                <div
                  className="flex justify-center items-center text-sm cursor-pointer hover:text-holos-dark"
                  onClick={() => setShowEnrollModal(true)}>
                  <CalendarIcon className="inline-block mr-2" width={15} />
                  Programar lo en el calendario
                </div>
              </div> */}
              {/* <span className="flex justify-center items-center text-sm cursor-pointer hover:text-blue-600">
                <Bookmark className="inline-block mr-2" width={15} />
                Guardarlo para más tarde
              </span> */}
              </div>
            )}

            {!videoFailed && !notFound && <div className="mx-auto mb-2 border-b border-gray-400" />}

            {!videoFailed && !notFound && (
            <div className="flex justify-between mb-3 my-3">
              {video.studio?.image && (
                <div className='mr-5'>
                  <div>
                    <a
                      style={{ backgroundImage: `url('https://image.beholos.com/fit-in/1200x750${video.studio.image}')` }}
                      className="block w-12 h-12 bg-cover bg-center rounded-full"
                      href={`/user/partners/${video.studio.slug}`}
                    ></a>
                  </div>
                </div>
              )}

              <div className="flex-1 relative">
                <div className="flex justify-between items-center">
                  {video.studio && (
                    <a
                      className="block text-xs font-medium uppercase text-holos-dark hover:underline"
                      href={`/user/partners/${video.studio.slug}`}
                    >
                      {video.studio.name}
                    </a>
                  )}
                  {video.saasCategory && (
                    <div className={`flex items-center px-3 py-2 rounded-full bg-${video.saasCategory.name.toLowerCase()}`}>
                      <div className="text-xs font-medium uppercase mr-1 text-black">
                        {video.saasCategory.name}
                      </div>
                      <img
                        src={`https://cache.beholos.com/img/fitness-pass-v2/corporate/saas-cat-${video.saasCategory.name.toLowerCase()}.svg`}
                        alt={video.saasCategory.name}
                        className="w-4 h-4"
                      />
                    </div>
                  )}
                </div>

                <div className="text-xl font-bold mb-2 font-juana">{video.title}</div>

                <div className="flex">
                  <div className="pr-2 border-r border-gray-300">
                    <span>
                      <NotebookPen className="inline-block mr-2" width={15} />
                    </span>
                    {video.teacher}
                  </div>
                  <div className="pl-2">
                    <span>
                      <ClockIcon className="inline-block mr-2 ml-2" width={15} />
                    </span>
                    {video.duration} min.
                  </div>
                  <span className="ml-2 pl-2 flex border-l border-gray-300 flex-shrink-0">
                    <svg
                      className="mr-2"
                      width="20.5"
                      height="20.5"
                      viewBox="0 0 24 24"
                      fill="#000"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M12 2.62978L14.6375 7.97394L14.7538 8.20966L15.014 8.24746L20.9116 9.10444L16.644 13.2643L16.4558 13.4478L16.5002 13.7069L17.5077 19.5807L12.2327 16.8074L12 16.6851L11.7673 16.8074L6.49232 19.5807L7.49976 13.7069L7.54419 13.4478L7.35596 13.2643L3.08839 9.10444L8.98603 8.24746L9.24616 8.20966L9.3625 7.97394L12 2.62978Z" stroke="black"></path>
                    </svg>
                    {(video.rating + 1).toFixed(1)}
                  </span>
                </div>

                {video.category && (
                  <div className="bg-gray-300 rounded-full p-2 px-4 my-2 text-xs inline-block">
                    {video.category.name}
                  </div>
                )}

                <div className="flex">
                  <span className="text-sm font-bold mr-2">Dificultad:</span>
                  {Array.from({ length: 4 - video.intensity }).map((_, idx) => (
                    <img
                      key={idx}
                      className="mr-1 w-4 h-4"
                      src="https://cache.beholos.com/img/fitness-pass-v2/global/flame-icon.svg"
                      alt="intensity"
                    />
                  ))}
                </div>

                <div className="my-2 flex items-center">
                  <span className="text-sm font-bold mr-2">Implementos necesarios:</span>
                  {video.equipment && video.equipment.length > 0 ? (
                    <span className="text-sm font-light">
                      {video.equipment.map((equipment: any, index: number) => (
                        <span key={equipment.name}>
                          {equipment.name}
                          {index !== video.equipment.length - 1 ? ', ' : ''}
                        </span>
                      ))}
                    </span>
                  ) : (
                    <span className="text-sm font-light">
                      Sin implementos
                    </span>
                  )}
                </div>

                <p className="mt-2 text-gray-600 text-sm">
                  {video.description}
                </p>
              </div>
            </div>
            )}

            {!videoFailed && <div className="mx-auto mt-2 mb-10 border-b border-gray-400" />}

            {!videoFailed && (
            <div className="w-full" id="video-reviews">
              <div className="mx-auto mb-5">
                {!previousReview ? (
                  <div className="mb-4">
                    <div className="font-juana font-bold">
                      ¿Cómo te fue en este video? ¡Tu comentario ayudará a más usuarios!
                    </div>
                    <div className="flex my-3">
                      <StarRating
                        rating={rating}
                        onRatingChange={setRating}
                        size={24}
                        className="mr-2"
                      />
                    </div>
                    <div className="mb-0">
                      <textarea
                        maxLength={300}
                        value={review}
                        onChange={(e) => setReview(e.target.value)}
                        className="w-full p-2 border-b resize-none focus:outline-none"
                        rows={4}
                        placeholder="Escribe un comentario aquí..."
                      />
                    </div>
                    <div className="flex items-end mb-4">
                      <button
                        onClick={onSubmitReview}
                        className="text-white uppercase bg-black py-2 px-8 hover:bg-gray-800 text-sm font-medium cursor-pointer mt-3 rounded">
                        Guardar
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-left text-lg text-gray-600 mb-4">
                    <InfoIcon className="inline-block mr-2" width={15} />
                    Gracias por comentar, tu opinión nos ayuda a mejorar
                  </div>
                )}

                <h4 className="font-medium text-lg font-juana mb-3 text-gray-500">
                  {reviews.length} Comentarios
                </h4>

                <div className="bg-white border border-gray-200 rounded-lg shadow-sm pt-2">
                  {reviews.map((review: any, index: number) => (
                    <div key={index}>
                      <div className="block w-full pt-2 pb-2 px-4">
                        <div className="flex flex-row">
                          <div className="w-20 flex-shrink-0">
                            <div className='mr-3'>
                              <img
                                className="rounded-full w-10 h-10 mx-auto block"
                                src={review.userImage || 'https://image.beholos.com/filters:quality(80)/img/userImage.jpg'}
                                alt="User avatar"
                              />
                              <div className="text-gray-600 text-xs text-center mt-1">
                                {(review.user || {}).firstname || ''}
                              </div>
                            </div>
                          </div>

                          <div className="flex-1 min-w-0 justify-center">
                            <div className="flex pt-2">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="#000" className="mr-1">
                                <path d="M12 2.62978L14.6375 7.97394L14.7538 8.20966L15.014 8.24746L20.9116 9.10444L16.644 13.2643L16.4558 13.4478L16.5002 13.7069L17.5077 19.5807L12.2327 16.8074L12 16.6851L11.7673 16.8074L6.49232 19.5807L7.49976 13.7069L7.54419 13.4478L7.35596 13.2643L3.08839 9.10444L8.98603 8.24746L9.24616 8.20966L9.3625 7.97394L12 2.62978Z" stroke="#000"></path>
                              </svg>
                              <strong className="mr-1">{review.rating + 1}.0</strong>
                            </div>
                            <p className="text-sm">{review.review}</p>
                          </div>

                          <div className="w-32 flex-shrink-0 text-right text-xs">
                            hace {formatDistanceToNow(new Date(review.date), { locale: es })}
                          </div>
                        </div>
                      </div>
                      <hr className="border-gray-200" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
            )}
          </div>
        )}

        <Modal
          isOpen={showVideoReview}
          onClose={onCancelReview}>
          <VideoReviewModal
            video={video}
            userFirstname={user?.firstname || ''}
            rating={rating}
            selectRating={setRating}
            review={review}
            onChangeReview={(e) => setReview(e.target.value)}
            message={message}
            onSubmitReview={onSubmitReview}
          />
        </Modal>

        <ShareVideoModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          videoSlug={video?.slug || ''}
        />
      </div>
    </div>
  );
}

export default VideoProfile;
